import { extractClosestEdge } from '@atlaskit/pragmatic-drag-and-drop-hitbox/closest-edge';
import { getReorderDestinationIndex } from '@atlaskit/pragmatic-drag-and-drop-hitbox/util/get-reorder-destination-index';
import type {
  BaseEventPayload,
  ElementDragType,
} from '@atlaskit/pragmatic-drag-and-drop/dist/types/internal-types';
import {
  toast,
  usePreferredNameLayout,
  wasMultiSelectKeyUsed,
} from '@tyro/core';
import { useTranslation } from '@tyro/i18n';
import cloneDeep from 'lodash/cloneDeep';
import {
  type Dispatch,
  type ReactNode,
  type SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  type EditedStudent,
  type ReturnTypeOfUseEditedState,
  type UseEditedStateProps,
  useEditedState,
} from './edited-state';
import type {
  ListManagerGroup,
  ListManagerState,
  ListManagerStudent,
} from './types';
import {
  type DraggableLocation,
  clearSameStudentsFromGroup,
  getGroupsWithDuplicates,
  multiDragAwareReorder,
  multiSelectTo,
  toggleSelectionInGroup,
} from './utils';

export interface UseListManagerStateProps {
  listKey: string;
  unassignedStudents: ListManagerStudent[];
  groups: ListManagerGroup[];
  onBulkSave: UseEditedStateProps['onBulkSave'];
}

export type ListManagerContextValue = {
  state: ListManagerState[];
  onDragStart: (args: BaseEventPayload<ElementDragType>) => void;
  onDragEnd: (args: BaseEventPayload<ElementDragType>) => void;
  editedState: ReturnTypeOfUseEditedState & {
    revertChange: (student: EditedStudent) => void;
  };
  draggingStudentId: string | undefined;
  performCardAction: (
    event: React.MouseEvent | React.KeyboardEvent | React.TouchEvent,
    studentId: string,
  ) => void;
  selectedStudentIds: string[];
  deleteDuplicate: (groupId: number | string, studentId: string) => void;
  duplicateStudents: (groupIdToMoveTo: number, studentIds: string[]) => void;
  moveStudents: (
    studentIds: string[],
    source: DraggableLocation,
    destination: DraggableLocation,
  ) => void;
  enableDuplicateStudents: boolean;
  includeClassGroupName: boolean;
  unassignedSearch: string;
  setUnassignedSearch: Dispatch<SetStateAction<string>>;
};

const ListManagerContext = createContext<ListManagerContextValue | undefined>(
  undefined,
);

type ListManagerProviderProps = {
  children: ReactNode;
  listKey: string;
  unassignedStudents: ListManagerStudent[];
  groups: ListManagerGroup[];
  onBulkSave: UseEditedStateProps['onBulkSave'];
  enableDuplicateStudents: boolean;
  includeClassGroupName: boolean;
};

export function ListManagerProvider({
  listKey,
  unassignedStudents,
  groups,
  onBulkSave,
  children,
  enableDuplicateStudents,
  includeClassGroupName,
}: ListManagerProviderProps) {
  const { t } = useTranslation(['classListManager']);
  const [unassignedSearch, setUnassignedSearch] = useState('');
  const { displayName } = usePreferredNameLayout();

  const lastEditedGroups = useRef<
    UseEditedStateProps['lastEditedGroups']['current']
  >({
    sourceIds: null,
    destinationId: null,
  });
  const [state, setState] = useState<ListManagerState[]>([]);
  const [draggingStudentId, setDraggingStudentId] = useState<string>();
  const [selectedStudentIds, setSelectedStudentIds] = useState<Array<string>>(
    [],
  );

  const resetBoard = () => {
    const mappedGroups =
      groups?.map((group) => ({
        ...group,
        id: group.partyId,
      })) ?? [];

    setState([
      {
        id: 'unassigned',
        name: t('classListManager:unassignedStudents'),
        students: unassignedStudents,
      },
      ...mappedGroups,
    ]);
  };

  const editedState = useEditedState({
    listKey,
    lastEditedGroups,
    state,
    onBulkSave,
    resetBoard,
  });

  const unselectAll = () => {
    setSelectedStudentIds([]);
  };

  const clearStudentsWithSamePartyId = useCallback(
    (groupsToCheck: ListManagerState[], groupId: string) => {
      const onDuplicateCleared = () => {
        toast.info(t('classListManager:weveTidiedStudentsInGroup'));
      };
      return clearSameStudentsFromGroup(
        groupsToCheck,
        groupId,
        onDuplicateCleared,
      );
    },
    [t],
  );

  const moveStudents = (
    studentIds: string[],
    source: DraggableLocation,
    destination: DraggableLocation,
  ) => {
    setState((prevState) => {
      const { newGroups, sourceGroupIds } = multiDragAwareReorder({
        groups: prevState,
        selectedStudentIds: studentIds,
        source,
        destination,
      });

      lastEditedGroups.current = {
        destinationId: destination.droppableId,
        sourceIds: sourceGroupIds,
      };

      return clearStudentsWithSamePartyId(newGroups, destination.droppableId);
    });
  };

  const onDragStart: ListManagerContextValue['onDragEnd'] = ({ source }) => {
    const id = String(source.data?.draggableId);
    const selected = selectedStudentIds.includes(id);
    if (!selected) {
      unselectAll();
    }
    setDraggingStudentId(id);
  };

  const onDragEnd: ListManagerContextValue['onDragEnd'] = ({
    source,
    location,
  }) => {
    const sourceData = source?.data;
    if (!sourceData) return;

    const from = {
      droppableId: String(sourceData.droppableId),
      index: Number(sourceData.index),
    };
    if (!from.droppableId || Number.isNaN(from.index)) return;

    const [target] = location.current.dropTargets || [];
    const targetData = target?.data;
    if (!targetData) return;

    const destinationDroppableId = String(targetData.droppableId);
    const indexOfTarget = Number(targetData.index ?? 0);
    if (!destinationDroppableId || Number.isNaN(indexOfTarget)) return;

    const closestEdge = extractClosestEdge(targetData);

    const toIndex =
      from.droppableId === destinationDroppableId
        ? getReorderDestinationIndex({
            startIndex: from.index,
            indexOfTarget,
            closestEdgeOfTarget: closestEdge,
            axis: 'vertical',
          })
        : closestEdge === 'bottom'
          ? indexOfTarget + 1
          : indexOfTarget;

    const to = {
      droppableId: destinationDroppableId,
      index: toIndex,
    };

    const isSamePosition =
      from.droppableId === to.droppableId && from.index === to.index;

    if (isSamePosition) return;

    moveStudents(selectedStudentIds, from, to);
    setDraggingStudentId(undefined);
  };

  const duplicateStudents = (groupIdToMoveTo: number, studentIds: string[]) => {
    lastEditedGroups.current = {
      destinationId: String(groupIdToMoveTo),
      sourceIds: null,
    };
    setState((prevState) => {
      const updatedGroups = getGroupsWithDuplicates(
        groupIdToMoveTo,
        studentIds,
        prevState,
      );

      return clearStudentsWithSamePartyId(
        updatedGroups,
        String(groupIdToMoveTo),
      );
    });
  };

  const deleteDuplicate = (groupId: number | string, studentId: string) => {
    lastEditedGroups.current = {
      destinationId: null,
      sourceIds: [String(groupId)],
    };
    setState((prevState) => {
      const newState = cloneDeep(prevState);
      const groupIndex = newState.findIndex(
        (group) => String(group.id) === String(groupId),
      );
      const studentIndex = newState[groupIndex].students.findIndex(
        (student) => student.id === studentId,
      );
      newState[groupIndex].students.splice(studentIndex, 1);
      return newState;
    });
  };

  const performCardAction = (
    event: React.MouseEvent | React.KeyboardEvent | React.TouchEvent,
    studentId: string,
  ) => {
    setSelectedStudentIds((selectedIds) => {
      if (wasMultiSelectKeyUsed(event)) {
        return multiSelectTo(
          studentId,
          selectedIds,
          state,
          unassignedSearch,
          displayName,
          includeClassGroupName,
        );
      }

      return toggleSelectionInGroup(studentId, selectedIds);
    });
  };

  const revertChange = ({
    student,
    sourceGroup,
    destinationGroup,
  }: EditedStudent) => {
    if (sourceGroup && destinationGroup) {
      const destinationGroupIndexFromState = state.findIndex(
        (group) => group.id === destinationGroup.id,
      );
      const currentStudentIndex = state[
        destinationGroupIndexFromState
      ].students.findIndex(({ id }) => id === student.id);
      moveStudents(
        [String(student.id)],
        {
          droppableId: String(destinationGroup.id),
          index: currentStudentIndex,
        },
        {
          droppableId: String(sourceGroup.id),
          index: 0,
        },
      );
    } else if (destinationGroup) {
      deleteDuplicate(destinationGroup.id, student.id);
    } else if (sourceGroup && sourceGroup.id !== 'unassigned') {
      duplicateStudents(sourceGroup.id, [String(student.person.partyId)]);
    }
  };

  useEffect(() => {
    resetBoard();
  }, [listKey]);

  useEffect(() => {
    const onWindowClickOrTouchEnd = (event: MouseEvent | TouchEvent) => {
      if (event.defaultPrevented) return;
      unselectAll();
    };

    const onWindowKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      if (event.key === 'Escape') {
        unselectAll();
      }
    };

    window.addEventListener('click', onWindowClickOrTouchEnd);
    window.addEventListener('touchend', onWindowClickOrTouchEnd);
    window.addEventListener('keydown', onWindowKeyDown);

    return () => {
      window.removeEventListener('click', onWindowClickOrTouchEnd);
      window.removeEventListener('touchend', onWindowClickOrTouchEnd);
      window.removeEventListener('keydown', onWindowKeyDown);
    };
  }, []);

  const providerValue = useMemo(
    () => ({
      state,
      onDragEnd,
      onDragStart,
      editedState: {
        ...editedState,
        revertChange,
      },
      draggingStudentId,
      performCardAction,
      selectedStudentIds,
      deleteDuplicate,
      duplicateStudents,
      moveStudents,
      enableDuplicateStudents,
      includeClassGroupName,
      unassignedSearch,
      setUnassignedSearch,
    }),
    [
      state,
      onDragEnd,
      onDragStart,
      editedState,
      revertChange,
      draggingStudentId,
      performCardAction,
      selectedStudentIds,
      deleteDuplicate,
      duplicateStudents,
      moveStudents,
      enableDuplicateStudents,
      includeClassGroupName,
      unassignedSearch,
      setUnassignedSearch,
    ],
  );

  return (
    <ListManagerContext.Provider value={providerValue}>
      {children}
    </ListManagerContext.Provider>
  );
}

export function useListManagerState() {
  const context = useContext(ListManagerContext);
  if (context === undefined) {
    throw new Error(
      'useListManagerState must be used within a ListManagerContext',
    );
  }
  return context;
}
